// HLS live playback for the Fan watch surface (ADR 0140). When a broadcast is
// delivered as HLS (the watch grant returns { mode: "hls", url }), the fan plays
// the segmented playlist in a plain <video> — which is what makes AirPlay / PiP /
// Chromecast fall out for free.
//
// Two paths, best-effort (a failure never blocks the surface; the pre-start /
// signal-lost slates still apply):
//   - Safari / iOS play HLS natively (canPlayType application/vnd.apple.mpegurl),
//     so we just set video.src — no library, no bundle cost.
//   - Chrome / Firefox need Media Source Extensions via hls.js, which is
//     dynamic-imported (code-split, never in the main bundle or in tests, mirroring
//     the MediaPipe lazy-load of ADR 0071) and attached to the element.

import { useEffect, useRef, useState } from "react";

/** Backoff cap between live re-attempts (LSC-436). */
export const LIVE_RETRY_MAX_MS = 10_000;

/**
 * liveRetryDelayMs is the wait before the Nth consecutive live re-attempt (0-based):
 * 1s, 2s, 4s, 8s, then capped at LIVE_RETRY_MAX_MS. A live playlist only exists once
 * the program-render egress has booted and written its first segment — seconds to tens
 * of seconds after go-live — and a fan who joins pre-TAKE asks for it in that window.
 * hls.js never retries a 4xx manifest and retries a 5xx only once by default, and a
 * native <video src> just errors, so without our own retry the fan sat on a dead
 * player for the whole show (the Sep-30 LSC-436 "m3u8 404").
 */
export function liveRetryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** Math.max(0, attempt), LIVE_RETRY_MAX_MS);
}

/** Reports whether the browser can play HLS from a <video src> directly (Safari). */
export function canPlayNativeHls(video: HTMLVideoElement): boolean {
  return video.canPlayType("application/vnd.apple.mpegurl") !== "";
}

/**
 * useHlsPlayback attaches an HLS playlist to a <video> while `active`, choosing
 * native playback or hls.js. It tears down the hls.js instance (and clears src) on
 * cleanup / when inputs change. `onError` is called for a fatal playback error.
 *
 * `lowLatency` marks a LIVE stream: on a fatal error the source is torn down and
 * re-attached after a capped backoff (liveRetryDelayMs), for as long as it stays
 * active, so a playlist that isn't produced yet (or a transient drop) recovers on its
 * own. The backoff resets once the stream loads. A VOD (lowLatency=false) keeps the
 * old behaviour: report the error and stop (the caller falls back to MP4).
 */
export function useHlsPlayback(
  videoRef: { current: HTMLVideoElement | null },
  url: string | null,
  active: boolean,
  onError?: (message: string) => void,
  lowLatency = false,
): void {
  // Keep the latest onError in a ref so a caller passing a fresh inline callback on
  // every render (e.g. VodPlayer re-rendered by a parent's live poll, LSC-419) does
  // NOT re-run this effect — re-running tears down + rebuilds the HLS source, which
  // restarts playback at 0:00. The effect depends only on [videoRef, url, active,
  // lowLatency] (+ the internal live re-attempt counter).
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // Live re-attempt (LSC-436): bumping `attempt` re-runs the effect, which tears the
  // source down and re-attaches it. `failures` counts consecutive failures for backoff.
  const [attempt, setAttempt] = useState(0);
  const failuresRef = useRef(0);
  // A new url/activation starts a fresh backoff sequence.
  useEffect(() => {
    failuresRef.current = 0;
  }, [url, active]);
  useEffect(() => {
    const video = videoRef.current;
    if (!active || !url || !video) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const fail = (message: string) => {
      onErrorRef.current?.(message);
      if (!lowLatency || retryTimer) return;
      const delay = liveRetryDelayMs(failuresRef.current);
      failuresRef.current += 1;
      retryTimer = setTimeout(() => setAttempt((n) => n + 1), delay);
    };
    const loaded = () => {
      failuresRef.current = 0;
    };
    const clearRetry = () => {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null;
    };

    // Native (Safari/iOS): the element handles HLS from src directly.
    if (canPlayNativeHls(video)) {
      video.src = url;
      const onErr = () => fail("hls native playback error");
      video.addEventListener("error", onErr);
      video.addEventListener("loadedmetadata", loaded);
      return () => {
        clearRetry();
        video.removeEventListener("error", onErr);
        video.removeEventListener("loadedmetadata", loaded);
        video.removeAttribute("src");
        try {
          video.load();
        } catch {
          // ignore
        }
      };
    }

    // MSE browsers: hls.js, loaded on demand.
    let destroyed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let hls: any = null;
    void import("hls.js")
      .then(({ default: Hls }) => {
        if (destroyed || !videoRef.current) return;
        if (!Hls.isSupported()) {
          // Last resort: try native src even if canPlayType was empty.
          videoRef.current.src = url;
          return;
        }
        // lowLatency only for a live LL-HLS stream. For a VOD playlist (ENDLIST),
        // lowLatencyMode makes hls.js live-sync — targeting a computed live edge and
        // re-targeting a manual seek back toward the start ≈ 0:00 (LSC-419 seek-snap).
        hls = new Hls({ enableWorker: true, lowLatencyMode: lowLatency });
        hls.on(Hls.Events.ERROR, (_evt: unknown, data: { fatal?: boolean; type?: string }) => {
          if (data?.fatal) fail(`hls error: ${data.type ?? "fatal"}`);
        });
        hls.on(Hls.Events.MANIFEST_PARSED, loaded);
        hls.loadSource(url);
        hls.attachMedia(videoRef.current);
      })
      .catch((e: unknown) => onErrorRef.current?.(`hls load failed: ${String(e)}`));

    return () => {
      destroyed = true;
      clearRetry();
      if (hls) {
        try {
          hls.destroy();
        } catch {
          // ignore
        }
        hls = null;
      }
      const v = videoRef.current;
      if (v) {
        v.removeAttribute("src");
        try {
          v.load();
        } catch {
          // ignore
        }
      }
    };
  }, [videoRef, url, active, lowLatency, attempt]);
}
